import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QadderLogo } from './Logo';
import { useLanguage } from './LanguageContext';
import {
  getQuestionsByMode,
  getTestIdByMode,
  createTrainingSession,
  completeSession,
  saveAnswer,
} from './supabaseClient';

// تنسيق الوقت
function formatTime(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── شريط الأسئلة السفلي ──
function QuestionNav({ total, cur, answers, onNavigate, sections }) {
  // eslint-disable-next-line no-unused-vars
  const { t } = useLanguage();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    const btn = scrollRef.current.querySelector(`[data-idx="${cur}"]`);
    if (btn) btn.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
  }, [cur]);

  // حدود الأقسام
  const sectionBreaks = sections.map((_, si) =>
    sections.slice(0, si).reduce((a, s) => a + s.count, 0)
  );

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
      background: 'var(--bg-header)',
      borderTop: '1px solid #334155',
      padding: '10px 12px 12px',
    }}>
      {/* أسماء الأقسام */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, justifyContent: 'center' }}>
        {sections.map((sec, si) => {
          const start = sectionBreaks[si];
          const answered = Array.from({ length: sec.count }, (_, i) => answers[start + i]).filter(x => x !== undefined).length;
          return (
            <div key={si} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: `${sec.color}18`, border: `1px solid ${sec.color}44`,
              borderRadius: 8, padding: '3px 10px',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: sec.color }} />
              <span style={{ color: sec.color, fontSize: 12, fontWeight: 700, fontFamily: 'Tajawal,sans-serif' }}>
                {sec.label}: {answered}/{sec.count}
              </span>
            </div>
          );
        })}
      </div>

      {/* شريط الأرقام */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex', gap: 6, overflowX: 'auto',
          paddingBottom: 4,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {Array.from({ length: total }, (_, i) => {
          const isCur = i === cur;
          const answered = answers[i] !== undefined;

          // تحديد لون القسم
          let secColor = '#3b82f6';
          let acc = 0;
          for (let s = 0; s < sections.length; s++) {
            if (i < acc + sections[s].count) { secColor = sections[s].color; break; }
            acc += sections[s].count;
          }

          // فاصل بين الأقسام
          const isSectionStart = sectionBreaks.includes(i) && i > 0;

          return (
            <React.Fragment key={i}>
              {isSectionStart && (
                <div style={{ width: 2, background: 'var(--border)', borderRadius: 2, flexShrink: 0, height: 36, alignSelf: 'center' }} />
              )}
              <button
                data-idx={i}
                onClick={() => onNavigate(i)}
                style={{
                  flexShrink: 0,
                  width: 36, height: 36,
                  borderRadius: 8,
                  background: isCur ? secColor : answered ? `${secColor}33` : 'var(--bg-card)',
                  border: isCur ? `2px solid ${secColor}` : answered ? `1px solid ${secColor}55` : '1px solid #334155',
                  color: isCur ? '#fff' : answered ? secColor : 'var(--text-muted)',
                  fontSize: 12, fontWeight: isCur || answered ? 800 : 500,
                  cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                  transition: 'all .15s',
                }}
              >
                {i + 1}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── شاشة النتيجة ──
function ResultScreen({ questions, answers, sections, duration, examDuration, onBack, onReview }) {
  const { t } = useLanguage();
  const totalAnswered = Object.keys(answers).length;
  const totalCorrect  = Object.entries(answers).filter(([idx, chosen]) =>
    chosen === Number(questions[idx]?.correct_key)
  ).length;
  const totalPct = questions.length > 0 ? Math.round((totalCorrect / questions.length) * 100) : 0;

  // نتائج كل قسم
  let acc = 0;
  const sectionResults = sections.map((sec) => {
    const start = acc;
    acc += sec.count;
    const correct = Array.from({ length: sec.count }, (_, i) => {
      const q = questions[start + i];
      const chosen = answers[start + i];
      return chosen !== undefined && chosen === Number(q?.correct_key);
    }).filter(Boolean).length;
    const answered = Array.from({ length: sec.count }, (_, i) => answers[start + i]).filter(x => x !== undefined).length;
    return { ...sec, correct, answered, pct: Math.round((correct / sec.count) * 100) };
  });

  const medal = totalPct >= 90 ? '🏆' : totalPct >= 70 ? '🥈' : totalPct >= 50 ? '💪' : '📚';
  const msg   = totalPct >= 90 ? t('exam_performExcellent')
              : totalPct >= 70 ? t('exam_performGood')
              : totalPct >= 50 ? t('exam_performOk')
              : t('exam_performBad');

  const timeTaken = (examDuration || duration) - duration;
  const minTaken  = Math.floor(timeTaken / 60);
  const secTaken  = timeTaken % 60;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      fontFamily: 'Tajawal,sans-serif', direction: 'rtl',
      padding: '24px 16px 40px',
    }}>
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        {/* العنوان */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 64 }}>{medal}</div>
          <h2 style={{ color: 'var(--text)', fontSize: 26, fontWeight: 900, margin: '8px 0 4px' }}>
            {t('exam_done')}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
            {minTaken} {t('trial_perQ')} {secTaken > 0 ? secTaken + t('trial_secUnit') : ''}
          </p>
        </div>

        {/* النتيجة الكلية */}
        <div style={{
          background: 'var(--bg-card)', borderRadius: 22, padding: '24px',
          border: '1px solid #334155', marginBottom: 16, textAlign: 'center',
        }}>
          <div style={{
            fontSize: 72, fontWeight: 900,
            background: totalPct >= 70 ? 'linear-gradient(90deg,#22c55e,#86efac)' :
                        totalPct >= 50 ? 'linear-gradient(90deg,#f59e0b,#fbbf24)' :
                                         'linear-gradient(90deg,#ef4444,#fca5a5)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            lineHeight: 1,
          }}>
            {totalPct}%
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: '8px 0 4px' }}>
            {totalCorrect} {t('study_correct')} {t('study_of')} {questions.length}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 16px' }}>
            {t('exam_answered2')} {totalAnswered} • {questions.length - totalAnswered} {t('exam_nav_unanswered')}
          </p>

          {/* شريط التقدم */}
          <div style={{ background: 'var(--bg-input)', borderRadius: 10, height: 12, overflow: 'hidden', marginBottom: 14 }}>
            <div style={{
              background: totalPct >= 70 ? 'linear-gradient(90deg,#22c55e,#86efac)' :
                          totalPct >= 50 ? 'linear-gradient(90deg,#f59e0b,#fbbf24)' :
                                           'linear-gradient(90deg,#ef4444,#fca5a5)',
              height: '100%', width: `${totalPct}%`, borderRadius: 10, transition: 'width 1.2s',
            }} />
          </div>

          <p style={{
            color: totalPct >= 70 ? '#86efac' : totalPct >= 50 ? '#fbbf24' : '#fca5a5',
            fontWeight: 700, fontSize: 15, margin: 0,
          }}>{msg}</p>
        </div>

        {/* نتائج الأقسام */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {sectionResults.map((sec, si) => (
            <div key={si} style={{
              background: 'var(--bg-card)', borderRadius: 16, padding: '16px 20px',
              border: `1px solid ${sec.color}33`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ color: sec.color, fontSize: 16, fontWeight: 800 }}>
                  {sec.label}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                  {sec.correct} {t('study_correct')} {t('study_of')} {sec.count} • {t('exam_answered2')} {sec.answered}
                </div>
                {/* شريط صغير */}
                <div style={{ background: 'var(--bg-input)', borderRadius: 6, height: 6, width: 160, marginTop: 8, overflow: 'hidden' }}>
                  <div style={{
                    background: sec.color, height: '100%',
                    width: `${sec.pct}%`, borderRadius: 6, transition: 'width 1s',
                  }} />
                </div>
              </div>
              <div style={{
                background: `${sec.color}22`, border: `1px solid ${sec.color}44`,
                borderRadius: 14, padding: '10px 16px', textAlign: 'center',
              }}>
                <div style={{ color: sec.color, fontSize: 26, fontWeight: 900, lineHeight: 1 }}>
                  {sec.pct}%
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* الأزرار */}
        <button onClick={onReview} style={{
          width: '100%', padding: 15,
          background: 'linear-gradient(135deg,#7c6df8,#a78bfa)',
          border: 'none', borderRadius: 14,
          color: '#fff', fontSize: 16, fontWeight: 900,
          cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
          marginBottom: 10,
          boxShadow: '0 4px 20px rgba(129,140,248,0.35)',
        }}>
          {t('exam_reviewAnswers')}
        </button>
        <button onClick={onBack} style={{
          width: '100%', padding: 14,
          background: 'var(--bg-card-alt)',
          border: '1px solid #334155', borderRadius: 14,
          color: 'var(--text-secondary)', fontSize: 16, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
        }}>
          {t('exam_backHome')}
        </button>
      </div>
    </div>
  );
}

// ── شاشة المراجعة ──
function ReviewScreen({ questions, answers, sections, onBack }) {
  const { t } = useLanguage();
  const [filter, setFilter] = useState('all'); // all | wrong | unanswered

  let acc = 0;
  const sectionRanges = sections.map((sec) => {
    const start = acc;
    acc += sec.count;
    return { ...sec, start, end: acc };
  });

  const filtered = questions
    .map((q, i) => {
      const chosen = answers[i];
      const correct = Number(q.correct_key);
      const isCorrect = chosen !== undefined && chosen === correct;
      const isUnanswered = chosen === undefined;
      const secInfo = sectionRanges.find(s => i >= s.start && i < s.end);
      return { q, i, chosen, correct, isCorrect, isUnanswered, secInfo };
    })
    .filter(({ isCorrect, isUnanswered }) => {
      if (filter === 'wrong') return !isCorrect && !isUnanswered;
      if (filter === 'unanswered') return isUnanswered;
      return true;
    });

  let opts = [];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      fontFamily: 'Tajawal,sans-serif', direction: 'rtl',
      padding: '0 0 40px',
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(13,17,23,0.95)', borderBottom: '1px solid #334155',
        padding: '14px 16px', position: 'sticky', top: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: '#7c6df8',
          fontSize: 18, cursor: 'pointer', padding: '0 8px 0 0',
        }}>←</button>
        <span style={{ color: 'var(--text)', fontWeight: 900, fontSize: 16 }}>{t('exam_reviewTitle')}</span>
        <div style={{ display: 'flex', gap: 6, marginRight: 'auto' }}>
          {[['all', t('exam_filterAll')],['wrong', t('exam_filterWrong')],['unanswered', t('exam_filterUnanswered')]].map(([f,l]) => (
            <button key={f} onClick={() => setFilter(f)} style={{
              background: filter === f ? '#7c6df8' : 'rgba(129,140,248,0.1)',
              border: `1px solid ${filter === f ? '#7c6df8' : 'var(--border)'}`,
              borderRadius: 8, padding: '4px 10px',
              color: filter === f ? '#fff' : 'var(--text-secondary)',
              fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
            }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px', maxWidth: 600, margin: '0 auto' }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 15 }}>{t('exam_noCategory')}</div>
          </div>
        ) : (
          filtered.map(({ q, i, chosen, correct, isCorrect, isUnanswered, secInfo }) => {
            try {
              if (Array.isArray(q.options)) opts = q.options;
              else if (typeof q.options === 'string') opts = JSON.parse(q.options);
              else opts = [];
            } catch { opts = []; }

            return (
              <div key={i} style={{
                background: 'var(--bg-card)', borderRadius: 16,
                border: `1px solid ${isUnanswered ? 'var(--border)' : isCorrect ? '#22c55e33' : '#ef444433'}`,
                padding: '16px 18px', marginBottom: 12,
              }}>
                {/* رقم السؤال + القسم */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    سؤال {i + 1}
                  </span>
                  <span style={{
                    background: `${secInfo?.color || '#7c6df8'}22`,
                    color: secInfo?.color || '#7c6df8',
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                  }}>{secInfo?.label}</span>
                </div>

                {/* نص السؤال */}
                <p style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.8 }}>
                  {q.question_ar}
                </p>

                {/* الخيارات */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {opts.map((opt, oi) => {
                    const isChosen  = oi === chosen;
                    const isCorrectOpt = oi === correct;
                    let bg = '#0d1117', border = 'var(--bg-card)', color = 'var(--text-muted)';
                    if (isCorrectOpt) { bg = '#14532d'; border = '#22c55e'; color = '#86efac'; }
                    else if (isChosen && !isCorrect) { bg = '#450a0a'; border = '#ef4444'; color = '#fca5a5'; }
                    return (
                      <div key={oi} style={{
                        background: bg, border: `1.5px solid ${border}`,
                        borderRadius: 10, padding: '9px 14px',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <span style={{
                          background: 'var(--bg-input)', borderRadius: 6,
                          padding: '1px 8px', fontSize: 11, color: 'var(--text-muted)',
                          fontFamily: 'Tajawal,sans-serif',
                        }}>
                          {['أ','ب','ج','د'][oi]}
                        </span>
                        <span style={{ color, fontSize: 14, flex: 1 }}>{opt}</span>
                        {isCorrectOpt && <span>✅</span>}
                        {isChosen && !isCorrectOpt && <span>❌</span>}
                      </div>
                    );
                  })}
                </div>

                {/* حالة إجابة المستخدم */}
                {isUnanswered && (
                  <div style={{ marginTop: 10, color: '#f59e0b', fontSize: 12 }}>
                    {t('exam_unansweredQ')}
                  </div>
                )}

                {/* الشرح */}
                {q.explanation && !q.explanation.includes('المصدر') && !q.explanation.includes('تسريبات') && (
                  <div style={{
                    marginTop: 10, background: 'var(--bg-input)', borderRadius: 8,
                    padding: '8px 12px', border: '1px solid #334155',
                  }}>
                    <p style={{ color: '#fbbf24', fontSize: 12, margin: 0 }}>💡 {q.explanation}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const EXAM_TYPES = {
  قدرات: {
    labelKey: 'exam_quantLabel',
    icon: '🎯',
    descKey: 'exam_quantDesc',
    color: '#7c6df8',
    duration: 7200,
    sections: [
      { mode: 'كمي',  count: 65,  labelKey: 'exam_quant',  color: '#3b82f6' },
      { mode: 'لفظي', count: 55,  labelKey: 'exam_verbal', color: '#7c6df8' },
    ],
  },
  تحصيلي: {
    labelKey: 'exam_tahsiliLabel',
    icon: '📚',
    descKey: 'exam_tahsiliDesc',
    color: '#f59e0b',
    duration: 7200,
    sections: [
      { mode: 'تحصيلي', count: 120, labelKey: 'exam_tahsiliLabel', color: '#f59e0b' },
    ],
  },
};

// ── شاشة اختيار نوع الاختبار ──
function SelectExamType({ onSelect, onBack }) {
  const { t, isRTL } = useLanguage();
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      fontFamily: 'Tajawal,sans-serif', direction: isRTL ? 'rtl' : 'ltr',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* شريط علوي */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid #1e2a42',
        background: 'rgba(13,17,23,0.95)',
      }}>
        <QadderLogo onHome={onBack} size={30} />
      </div>

      {/* محتوى متوسط */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
      }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏆</div>
      <h2 style={{ color: 'var(--text)', fontSize: 24, fontWeight: 900, margin: '0 0 6px', textAlign: 'center' }}>
        {t('exam_title')}
      </h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 32px', textAlign: 'center' }}>
        {t('exam_selectExam')}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%', maxWidth: 400 }}>
        {Object.entries(EXAM_TYPES).map(([key, ex]) => (
          <button key={key} onClick={() => onSelect(key)} style={{
            background: `${ex.color}12`,
            border: `2px solid ${ex.color}44`,
            borderRadius: 18, padding: '22px 24px',
            cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
            display: 'flex', alignItems: 'center', gap: 16,
            transition: 'all .2s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = ex.color}
          onMouseLeave={e => e.currentTarget.style.borderColor = `${ex.color}44`}
          >
            <span style={{ fontSize: 36 }}>{ex.icon}</span>
            <div style={{ textAlign: isRTL ? 'right' : 'left', flex: 1 }}>
              <div style={{ color: ex.color, fontWeight: 900, fontSize: 18 }}>{t(ex.labelKey)}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 3 }}>{t(ex.descKey)}</div>
            </div>
            <span style={{ color: ex.color, fontSize: 20 }}>{isRTL ? '←' : '→'}</span>
          </button>
        ))}
      </div>

      <button onClick={onBack} style={{
        marginTop: 24, background: 'none', border: 'none',
        color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
      }}>
        {t('exam_back')}
      </button>
      </div> {/* end centered content */}
    </div>
  );
}

// ── الشاشة الرئيسية للاختبار ──
export default function ExamScreen({ user, onBack }) {
  const [examTypeKey, setExamTypeKey] = useState(null);

  if (!examTypeKey) {
    return <SelectExamType onSelect={setExamTypeKey} onBack={onBack} />;
  }

  return <ExamRunner user={user} onBack={onBack} examTypeKey={examTypeKey} />;
}

function ExamRunner({ user, onBack, examTypeKey }) {
  const { t } = useLanguage();
  const examType = EXAM_TYPES[examTypeKey];
  const SECTIONS = examType.sections.map(s => ({ ...s, label: t(s.labelKey) }));
  const EXAM_DURATION = examType.duration;

  const [screen, setScreen]       = useState('loading'); // loading | exam | confirm | result | review
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers]     = useState({});        // { idx: chosenIndex }
  const [cur, setCur]             = useState(0);
  const [timeLeft, setTimeLeft]   = useState(EXAM_DURATION);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [showGrid, setShowGrid]   = useState(false);

  const sessionId    = useRef(null);
  const sessionStart = useRef(Date.now());
  const answersRef   = useRef({});
  const timeRef      = useRef(EXAM_DURATION);

  // ── تحميل الأسئلة ──
  useEffect(() => {
    const load = async () => {
      let allQ = [];
      for (const sec of SECTIONS) {
        setLoadingMsg(`${sec.label}... (${sec.count})`);
        const { data } = await getQuestionsByMode(sec.mode, sec.count, null, []);
        allQ = [...allQ, ...(data || []).slice(0, sec.count)];
      }

      if (!allQ.length) { setScreen('empty'); return; }

      setQuestions(allQ);

      // إنشاء جلسة
      if (user?.id) {
        const testId = await getTestIdByMode('كمي');
        if (testId) {
          const { data: sess } = await createTrainingSession(user.id, testId, 'exam');
          if (sess) sessionId.current = sess.id;
        }
      }

      sessionStart.current = Date.now();
      setScreen('exam');
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── العداد التنازلي ──
  useEffect(() => {
    if (screen !== 'exam') return;
    if (timeLeft <= 0) {
      handleSubmit(true);
      return;
    }
    const t = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1;
        timeRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, timeLeft]);

  const handleSelect = useCallback((chosenIdx) => {
    setAnswers(prev => {
      const next = { ...prev, [cur]: chosenIdx };
      answersRef.current = next;
      return next;
    });
  }, [cur]);

  const handleSubmit = useCallback(async (auto = false) => {
    setScreen('result');
    const finalAnswers = answersRef.current;
    const correct = Object.entries(finalAnswers).filter(([idx, chosen]) => {
      const q = questions[idx];
      return q && chosen === Number(q.correct_key);
    }).length;
    const total = questions.length;
    const durationSecs = Math.round((Date.now() - sessionStart.current) / 1000);

    if (sessionId.current) {
      await completeSession(sessionId.current, correct, total, durationSecs);
      // حفظ الإجابات
      for (const [idx, chosen] of Object.entries(finalAnswers)) {
        const q = questions[idx];
        if (q) {
          await saveAnswer(sessionId.current, q.id, chosen, chosen === Number(q.correct_key), 0);
        }
      }
    }
  }, [questions]);

  // الحصول على خيارات السؤال الحالي
  let currentOpts = [];
  if (questions[cur]) {
    try {
      if (Array.isArray(questions[cur].options)) currentOpts = questions[cur].options;
      else if (typeof questions[cur].options === 'string') currentOpts = JSON.parse(questions[cur].options);
    } catch { currentOpts = []; }
  }

  const answeredCount = Object.keys(answers).length;
  const timerColor = timeLeft > 1800 ? '#22c55e' : timeLeft > 600 ? '#f59e0b' : '#ef4444';
  const currentQ = questions[cur];

  // تحديد قسم السؤال الحالي
  let curSection = SECTIONS[0];
  let acc2 = 0;
  for (const sec of SECTIONS) {
    if (cur < acc2 + sec.count) { curSection = sec; break; }
    acc2 += sec.count;
  }

  // ── شاشة التحميل ──
  if (screen === 'loading') return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-page)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Tajawal,sans-serif', gap: 16,
    }}>
      <div style={{ fontSize: 56 }}>🎯</div>
      <div style={{ color: '#7c6df8', fontSize: 18, fontWeight: 700 }}>
        {t('exam_title')} — {t(examType.labelKey)}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{loadingMsg}</div>
      <div style={{
        width: 200, height: 6, background: 'var(--bg-card)', borderRadius: 6, overflow: 'hidden', marginTop: 8,
      }}>
        <div style={{
          height: '100%', borderRadius: 6,
          background: 'linear-gradient(90deg,#7c6df8,#a78bfa)',
          animation: 'pulse 1.5s ease-in-out infinite',
          width: '60%',
        }} />
      </div>
    </div>
  );

  // ── لا توجد أسئلة ──
  if (screen === 'empty') return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-page)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Tajawal,sans-serif', gap: 12,
    }}>
      <div style={{ fontSize: 48 }}>📭</div>
      <div style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700 }}>{t('exam_noQuestions')}</div>
      <button onClick={onBack} style={{
        background: 'var(--border)', border: 'none', borderRadius: 12, padding: '12px 28px',
        color: 'var(--text)', fontSize: 15, cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
      }}>{t('exam_back')}</button>
    </div>
  );

  // ── شاشة النتيجة ──
  if (screen === 'result') return (
    <ResultScreen
      questions={questions}
      answers={answers}
      sections={SECTIONS}
      duration={timeLeft}
      examDuration={EXAM_DURATION}
      onBack={onBack}
      onReview={() => setScreen('review')}
    />
  );

  // ── شاشة المراجعة ──
  if (screen === 'review') return (
    <ReviewScreen
      questions={questions}
      answers={answers}
      sections={SECTIONS}
      onBack={() => setScreen('result')}
    />
  );

  // ── الاختبار ──
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      fontFamily: 'Tajawal,sans-serif', direction: 'rtl',
      paddingBottom: 100,
    }}>

      {/* ── ديالوج تأكيد التسليم ── */}
      {screen === 'confirm' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            background: 'var(--bg-page)',
            border: '1px solid #334155', borderRadius: 24,
            padding: '32px 28px', width: '100%', maxWidth: 400,
            direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>📤</div>
            <h3 style={{ color: 'var(--text)', fontSize: 20, fontWeight: 900, margin: '0 0 8px' }}>
              {t('exam_submitExam')}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 6px', lineHeight: 1.7 }}>
              {t('exam_answered2')} <span style={{ color: '#22c55e', fontWeight: 900 }}>{answeredCount}</span> {t('study_of')}{' '}
              <span style={{ color: '#7c6df8', fontWeight: 900 }}>{questions.length}</span> {t('study_question')}
            </p>
            {questions.length - answeredCount > 0 && (
              <p style={{ color: '#f59e0b', fontSize: 13, margin: '0 0 20px' }}>
                ⚠️ {questions.length - answeredCount} {t('exam_unansweredWarning')}
              </p>
            )}
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 24px' }}>
              {t('exam_timeLeft')} <span style={{ color: timerColor, fontWeight: 700 }}>{formatTime(timeLeft)}</span>
            </p>
            <button onClick={() => handleSubmit(false)} style={{
              width: '100%', padding: 15,
              background: 'linear-gradient(135deg,#22c55e,#16a34a)',
              border: 'none', borderRadius: 13,
              color: '#fff', fontSize: 16, fontWeight: 900,
              cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
              marginBottom: 10,
              boxShadow: '0 4px 18px rgba(34,197,94,0.35)',
            }}>
              {t('exam_submitNow')}
            </button>
            <button onClick={() => setScreen('exam')} style={{
              width: '100%', padding: 14,
              background: 'var(--bg-card-alt)',
              border: '1px solid #334155', borderRadius: 13,
              color: 'var(--text-secondary)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
            }}>
              {t('exam_cancelContinue')}
            </button>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{
        background: 'var(--bg-header)',
        borderBottom: '1px solid #334155',
        padding: '10px 16px',
        position: 'sticky', top: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* الوقت */}
        <div style={{
          background: `${timerColor}18`,
          border: `1.5px solid ${timerColor}55`,
          borderRadius: 12, padding: '6px 14px',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ fontSize: 14 }}>⏱️</span>
          <span style={{
            color: timerColor, fontSize: 20, fontWeight: 900,
            fontFamily: 'monospace', letterSpacing: 1,
          }}>
            {formatTime(timeLeft)}
          </span>
        </div>

        {/* رقم السؤال + زر الشبكة */}
        <button
          onClick={() => setShowGrid(true)}
          style={{
            textAlign: 'center', background: 'rgba(124,109,248,0.1)',
            border: '1px solid rgba(124,109,248,0.3)',
            borderRadius: 12, padding: '6px 14px', cursor: 'pointer',
            fontFamily: 'Tajawal,sans-serif',
          }}
        >
          <div style={{ color: curSection.color, fontSize: 11, fontWeight: 700 }}>
            {curSection.label}
          </div>
          <div style={{ color: 'var(--text)', fontSize: 15, fontWeight: 900 }}>
            📋 {cur + 1} / {questions.length}
          </div>
        </button>

        {/* زر التسليم */}
        <button onClick={() => setScreen('confirm')} style={{
          background: 'linear-gradient(135deg,#22c55e,#16a34a)',
          border: 'none', borderRadius: 10,
          color: '#fff', fontSize: 13, fontWeight: 900,
          cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
          padding: '8px 14px',
          boxShadow: '0 3px 12px rgba(34,197,94,0.3)',
        }}>
          {t('exam_submitBtn')}
        </button>
      </div>

      {/* ── شريط الإجابات المكتملة ── */}
      <div style={{
        background: 'var(--bg-input)', height: 4,
        position: 'sticky', top: 52, zIndex: 9,
      }}>
        <div style={{
          background: 'linear-gradient(90deg,#3b82f6,#7c6df8)',
          height: '100%',
          width: `${(answeredCount / questions.length) * 100}%`,
          transition: 'width .3s',
        }} />
      </div>

      {/* ── شبكة الأسئلة (popup) ── */}
      {showGrid && (
        <div
          onClick={() => setShowGrid(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)', border: '1px solid #1e2a42',
              borderRadius: 22, padding: '20px 18px',
              width: '100%', maxWidth: 480,
              direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
              maxHeight: '80vh', overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 16, fontWeight: 900 }}>
                جميع الأسئلة ({Object.keys(answers).length}/{questions.length})
              </h3>
              <button onClick={() => setShowGrid(false)} style={{
                background: 'none', border: 'none', color: 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 20, lineHeight: 1,
              }}>✕</button>
            </div>

            {/* مفتاح الألوان */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              {[['#7c6df8', t('exam_nav_current')],['#3fb950', t('exam_nav_answered')],['var(--border)', t('exam_nav_unanswered')]].map(([c,l]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 4, background: c }} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{l}</span>
                </div>
              ))}
            </div>

            {/* الشبكة لكل قسم */}
            {SECTIONS.map((sec, si) => {
              const start = SECTIONS.slice(0, si).reduce((a, s) => a + s.count, 0);
              const answeredInSec = Array.from({ length: sec.count }, (_, i) => answers[start + i]).filter(x => x !== undefined).length;
              return (
                <div key={si} style={{ marginBottom: 16 }}>
                  <div style={{
                    color: sec.color, fontSize: 13, fontWeight: 800,
                    marginBottom: 8, display: 'flex', justifyContent: 'space-between',
                  }}>
                    <span>{sec.label}</span>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{answeredInSec}/{sec.count}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {Array.from({ length: sec.count }, (_, i) => {
                      const idx = start + i;
                      const isCur = idx === cur;
                      const answered = answers[idx] !== undefined;
                      return (
                        <button
                          key={idx}
                          onClick={() => { setCur(idx); setShowGrid(false); }}
                          style={{
                            width: 38, height: 38, borderRadius: 9,
                            background: isCur ? '#7c6df8' : answered ? `${sec.color}33` : '#0d1117',
                            border: isCur ? '2px solid #7c6df8' : answered ? `1px solid ${sec.color}66` : '1px solid #1e2a42',
                            color: isCur ? '#fff' : answered ? sec.color : 'var(--text-muted)',
                            fontSize: 12, fontWeight: isCur || answered ? 800 : 400,
                            cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                            transition: 'all .12s',
                          }}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* إحصائيات سريعة */}
            <div style={{
              marginTop: 12, padding: '12px 16px',
              background: 'var(--bg-input)', borderRadius: 12,
              display: 'flex', justifyContent: 'space-around',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#3fb950', fontWeight: 900, fontSize: 20 }}>{Object.keys(answers).length}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('exam_nav_answered')}</div>
              </div>
              <div style={{ width: 1, background: 'var(--border)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#f85149', fontWeight: 900, fontSize: 20 }}>{questions.length - Object.keys(answers).length}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{t('exam_nav_remaining')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── جسم السؤال ── */}
      <div style={{ padding: '20px 16px', maxWidth: 600, margin: '0 auto' }}>

        {/* نص السؤال */}
        <div style={{
          background: 'var(--bg-card)', borderRadius: 18, padding: '20px 22px',
          border: `1px solid ${curSection.color}33`, marginBottom: 16,
        }}>
          <p style={{
            color: 'var(--text)', fontSize: 19, fontWeight: 700,
            margin: 0, lineHeight: 1.9,
          }}>
            {currentQ?.question_ar}
          </p>
          {currentQ?.image_url && (
            <img
              src={currentQ.image_url}
              alt="صورة السؤال"
              style={{
                marginTop: 14, width: '100%', borderRadius: 10,
                border: '1px solid #334155', objectFit: 'contain',
                maxHeight: 280, background: '#fff',
              }}
            />
          )}
        </div>

        {/* الخيارات */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {currentOpts.map((opt, oi) => {
            const isSelected = answers[cur] === oi;
            return (
              <button
                key={oi}
                onClick={() => handleSelect(oi)}
                style={{
                  background: isSelected ? `${curSection.color}22` : 'var(--bg-card)',
                  border: `2px solid ${isSelected ? curSection.color : 'var(--border)'}`,
                  borderRadius: 12, padding: '14px 16px',
                  color: isSelected ? 'var(--text)' : '#cbd5e1',
                  fontSize: 16, fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                  textAlign: 'right', direction: 'rtl',
                  display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'all .15s',
                }}
              >
                <span style={{
                  background: isSelected ? curSection.color : '#0d1117',
                  color: isSelected ? '#fff' : 'var(--text-muted)',
                  borderRadius: 7, padding: '2px 9px',
                  fontSize: 12, minWidth: 24, textAlign: 'center',
                  fontFamily: 'Tajawal,sans-serif', flexShrink: 0,
                  transition: 'all .15s',
                }}>
                  {['أ','ب','ج','د'][oi]}
                </span>
                <span style={{ flex: 1 }}>{opt}</span>
                {isSelected && <span style={{ color: curSection.color, fontSize: 16 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* أزرار التنقل */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={() => setCur(c => Math.max(0, c - 1))}
            disabled={cur === 0}
            style={{
              flex: 1, padding: 13,
              background: cur === 0 ? '#0d1117' : 'var(--bg-card-alt)',
              border: '1px solid #334155', borderRadius: 12,
              color: cur === 0 ? 'var(--border)' : 'var(--text-secondary)',
              fontSize: 15, fontWeight: 700,
              cursor: cur === 0 ? 'not-allowed' : 'pointer',
              fontFamily: 'Tajawal,sans-serif',
            }}
          >
            → السابق
          </button>
          <button
            onClick={() => setCur(c => Math.min(questions.length - 1, c + 1))}
            disabled={cur === questions.length - 1}
            style={{
              flex: 1, padding: 13,
              background: cur === questions.length - 1 ? '#0d1117' : `${curSection.color}18`,
              border: `1px solid ${cur === questions.length - 1 ? 'var(--border)' : curSection.color + '44'}`,
              borderRadius: 12,
              color: cur === questions.length - 1 ? 'var(--border)' : curSection.color,
              fontSize: 15, fontWeight: 700,
              cursor: cur === questions.length - 1 ? 'not-allowed' : 'pointer',
              fontFamily: 'Tajawal,sans-serif',
            }}
          >
            التالي ←
          </button>
        </div>

        {/* إحصائيات سريعة */}
        <div style={{
          display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid #334155',
            borderRadius: 10, padding: '8px 14px', textAlign: 'center', flex: 1,
          }}>
            <div style={{ color: '#22c55e', fontWeight: 900, fontSize: 18 }}>{answeredCount}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>أجبت</div>
          </div>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid #334155',
            borderRadius: 10, padding: '8px 14px', textAlign: 'center', flex: 1,
          }}>
            <div style={{ color: '#f59e0b', fontWeight: 900, fontSize: 18 }}>{questions.length - answeredCount}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>متبقي</div>
          </div>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid #334155',
            borderRadius: 10, padding: '8px 14px', textAlign: 'center', flex: 1,
          }}>
            <div style={{ color: '#7c6df8', fontWeight: 900, fontSize: 18 }}>{questions.length}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>المجموع</div>
          </div>
        </div>
      </div>

      {/* ── شريط التنقل السفلي ── */}
      <QuestionNav
        total={questions.length}
        cur={cur}
        answers={answers}
        onNavigate={setCur}
        sections={SECTIONS}
      />
    </div>
  );
}
